import React, { useState, useEffect, useCallback } from 'react'
import { CCard, CCardTitle, CImage } from '@coreui/react-pro'
import './ProcedureCard.scss'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import ButtonGroup from '../common/ButtonGroup/ButtonGroup'
import tick from '../../assets/icons/Encounter-Progress.svg'
import aiTick from '../../assets/icons/Encounter-ProgressAI.svg'
import CardListProcedureDiagnosis from '../common/CardListProcedureDiagnosis/CardListProcedureDiagnosis'
import CardDiagnosisProcedure from '../common/CardDiagnosisProcedure/CardDiagnosisProcedure'
import { getFavProcedures } from 'src/helper/backendHelper'
import _ from 'lodash'
import {
  getChartValidations,
  initialCodesAllowed,
  subsequentCodesAllowed,
} from '../../helper/chartValidationHelper'
import CodeSearchBox from '../common/SearchBoxEncounter/CodeSearchBox'
import { WHITE } from '../../scss/colorConsts'
import { addCPTVerification, cptSearch } from '../common/shared/sharedCPTLogic'
import { getIsAi } from '../../utils/sharedFeaturesFunctions'
import { ENCOUNTER_TYPES } from '../../views/pages/sharedConstants'

const EDCCCodes = ['99281', '99282', '99283', '99284', '99285', '99291', '99292']

function evaluateCPTsToShow(
  searchText,
  searchResults,
  favorites,
  currentCodeTemplate,
  currentCodeTemplateResults,
) {
  if (searchText) {
    return searchResults
  } else if (currentCodeTemplate === 'Favorites') {
    return Object.values(favorites)
  } else if (currentCodeTemplate) {
    return currentCodeTemplateResults
  } else {
    // no text or filter -> smartCPT with a default to favorites
    return searchResults.length > 0 ? searchResults : Object.values(favorites)
  }
}

async function fetchDetailedTemplateData(currentCodeTemplate, callback) {
  // if favorites, we already have them locally, so don't call for them
  if (currentCodeTemplate === 'Favorites') {
    return callback([])
  }

  await getFavProcedures({
    template: currentCodeTemplate,
  }).then((res) => {
    if (res?.status === 'success') {
      return callback(res.results)
    }
    return callback([])
  })
}

const ProcedureCard = ({
  selectedProcedure,
  setSelectedProcedure,
  noEncounter,
  noIndicater,
  isProgressNote,
  title,
  pos,
  // represents encounterType
  initialBtn,
  setIntialBtn = () => {},
  encounterHistoryData,
  dateOfService,
  processStop,
  shouldFocus = true,
}) => {
  const thisDate = useSelector((state) => state?.census?.activeDate) || new Date()
  const provFavCPTs = useSelector((state) => state.user.providerFavCPT)

  const [searchText, setSearchText] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [currentCodeTemplate, setCurrentCodeTemplate] = useState(null)
  const [currentCodeTemplateResults, setCurrentCodeTemplateResults] = useState([])

  const [procedureSelected, setProcedureSelected] = useState(false)
  const [selectedEncounterType, setSelectedEncounterType] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const displayedCPTs = evaluateCPTsToShow(
    searchText,
    searchResult,
    provFavCPTs,
    currentCodeTemplate,
    currentCodeTemplateResults,
  )

  useEffect(() => {
    // if no encounter type has been specified and
    // if there is an encounter history and
    // if the most recent encounter was before today and
    // if the current census day is not before the last encounter and
    // if the last status was NOT Discharge
    //
    // then update to status to subsequent
    // This should only affect currently-being-created encounters

    // if an ED or critical care code is selected, always default to other
    if (selectedProcedure?.some((procedure) => EDCCCodes?.includes(procedure?.code))) {
      setIntialBtn(ENCOUNTER_TYPES.OTHER)
      setSelectedEncounterType(true)
    } else if (
      !selectedEncounterType &&
      encounterHistoryData?.length > 0 &&
      new Date(encounterHistoryData[0]?.service_date_from) < new Date() &&
      new Date(encounterHistoryData[0]?.service_date_from) < new Date(thisDate) &&
      encounterHistoryData[0]?.encounterType !== ENCOUNTER_TYPES.DISCHARGE
    ) {
      setIntialBtn(ENCOUNTER_TYPES.SUBSEQUENT)
    }
  }, [encounterHistoryData, selectedProcedure])

  const [validationDirectives, setValidationDirectives] = useState(
    JSON.parse(sessionStorage.getItem('directives/chart_validation')),
  )

  useEffect(() => {
    const fetchValidationDirectives = async () => {
      const validationDirectives = await getChartValidations()
      setValidationDirectives(validationDirectives)
    }

    if (!validationDirectives) {
      fetchValidationDirectives().catch((err) =>
        console.error(`fetchValidationDirectives failed: ${err}`),
      )
    }
  }, [validationDirectives])

  const checkErrorMessage = () => {
    if (initialBtn === ENCOUNTER_TYPES.INITIAL) {
      if (
        selectedProcedure?.length > 0 &&
        !initialCodesAllowed(validationDirectives).includes(selectedProcedure[0].code)
      ) {
        processStop(false)
        setErrorMessage('1st procedure code selected is not valid for initial encounter')
      } else {
        processStop(true)
        setErrorMessage('')
      }
    } else if (initialBtn === ENCOUNTER_TYPES.SUBSEQUENT) {
      if (
        selectedProcedure?.length > 0 &&
        !subsequentCodesAllowed(validationDirectives).includes(selectedProcedure[0].code)
      ) {
        processStop(false)
        setErrorMessage('1st procedure code selected is not valid for subsequent encounter')
      } else {
        processStop(true)
        setErrorMessage('')
      }
    } else {
      processStop(true)
      setErrorMessage('')
    }
  }

  useEffect(() => {
    if (validationDirectives) {
      checkErrorMessage()
    }
  }, [validationDirectives, initialBtn, selectedProcedure])

  useEffect(() => {
    setProcedureSelected(selectedProcedure.length > 0)
  }, [selectedProcedure])

  const searchData = async (text, pos, initialBtn) => {
    const newCPTs = await cptSearch({
      searchString: text,
      pos: pos,
      encounterType: initialBtn,
    })

    let searchedResult = (newCPTs || []).toSorted((a, b) => {
      if (a.initial_code && !b.initial_code) {
        return initialBtn === ENCOUNTER_TYPES.INITIAL ? -1 : 1
      } else if (!a.initial_code && b.initial_code) {
        return initialBtn === ENCOUNTER_TYPES.INITIAL ? 1 : -1
      }

      return 0
    })

    setSearchResult(searchedResult)
  }

  const searchTextCallback = useCallback(
    _.debounce((searchText, pos, initialBtn) => {
      searchData(searchText, pos, initialBtn)
    }, 700),
    [],
  )

  useEffect(() => {
    searchTextCallback(searchText, pos, initialBtn)
  }, [searchText, pos, initialBtn, searchTextCallback])

  const handleAdd = (item) => {
    addCPTVerification(selectedProcedure, item, setSelectedProcedure)
  }

  const onModifierAdd = (item) => {
    if (item !== undefined) {
      const selectedListWithMod = [...selectedProcedure]
      let index = selectedProcedure.findIndex((code) => code.code === item.code)

      if (index > -1) {
        selectedListWithMod[index] = item
      }

      setSelectedProcedure(selectedListWithMod)
    }
  }

  const onDragStart = (ev, datacodeProcedure) => {
    ev.dataTransfer.setData('datacodeProcedure', datacodeProcedure)
  }

  const onDrop = (ev) => {
    let datacodeProcedure = ev.dataTransfer.getData('datacodeProcedure')
    let newProcedure = displayedCPTs?.find(
      (procedure) =>
        procedure.code + '-' + (procedure?.modifiers || []).join('-') === datacodeProcedure,
    )

    addCPTVerification(selectedProcedure, newProcedure, setSelectedProcedure)

    checkErrorMessage()
  }

  return (
    <CCard className="ProcedureCard">
      <div className="ProceduredHeaderContainer">
        <div className="ProcedureCardHeader">
          {!noIndicater && (
            <>
              {procedureSelected && errorMessage === '' ? (
                <CImage className="tick-size-proc" src={getIsAi() ? aiTick : tick} />
              ) : (
                <span className="encounter-progress-number nonactive-number-progress-encounter"></span>
              )}
            </>
          )}

          <CCardTitle className="ProcedureCardTitle">
            {title ?? 'Procedure Codes'}
            {errorMessage && (
              <span
                style={{
                  background: 'red',
                  color: WHITE,
                  fontSize: '14px',
                  padding: '5px',
                  borderRadius: '6px',
                  marginLeft: '10px',
                }}
              >
                <i className="fa-solid fa-triangle-exclamation"></i>
                {errorMessage}
              </span>
            )}
          </CCardTitle>
        </div>
        {!noIndicater && selectedProcedure.length > 0 && errorMessage === '' && (
          <span className="completed-container completed-text">COMPLETED</span>
        )}
      </div>
      <div>
        {!noEncounter && (
          <>
            <div className="encounter-type">Encounter type</div>
            <div className="encounter-item-margin">
              <div className="btn-group encounter-details-btn" role="group">
                <ButtonGroup
                  value={Object.values(ENCOUNTER_TYPES)}
                  selected={initialBtn}
                  color="primary"
                  onChange={(e) => {
                    setSelectedEncounterType(true)
                    if (setIntialBtn) {
                      setIntialBtn(e)
                    }
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className="Procedure-card-search">
        <div className="column1">
          <CodeSearchBox
            searchText={searchText}
            setSearchText={setSearchText}
            shouldFocus={shouldFocus}
            currentCodeTemplate={currentCodeTemplate}
            setCurrentCodeTemplate={(newTemplate) => {
              if (newTemplate) {
                fetchDetailedTemplateData(newTemplate, setCurrentCodeTemplateResults)
              }
              setCurrentCodeTemplate(newTemplate)
            }}
            type={'procedures'}
          />
        </div>
        <div className="Procedure-card-select column2">
          Selected procedures ({selectedProcedure?.filter((item) => item?.code).length})
        </div>
      </div>

      <div className="Procedure-card-select-card">
        {displayedCPTs?.length > 0 ? (
          <div className="proc-card-scroll column1">
            {displayedCPTs.map((cpt, index) => {
              let key = cpt.code + '-' + (cpt?.modifiers || []).join('-')
              return (
                <div key={key} onDragStart={(e) => onDragStart(e, key)} draggable>
                  <CardDiagnosisProcedure
                    item={cpt}
                    index={index}
                    handleAddRemove={handleAdd}
                    onModifierAdd={onModifierAdd}
                    icon={
                      selectedProcedure?.find((sel) => sel.code === cpt.code) ? 'minus' : 'plus'
                    }
                    hideModifier={true}
                    toggleFavorite={true}
                    selectedList={selectedProcedure}
                    type={'PROCEDURE'}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="column1 noResults">
            <p>No results found</p>
          </div>
        )}
        <div
          className={
            isProgressNote
              ? 'proc-card-scroll-progress column2-proc-progress'
              : 'proc-card-scroll column2'
          }
          onDragOver={(e) => {
            e.preventDefault()
          }}
          onDrop={(e) => onDrop(e)}
        >
          <CardListProcedureDiagnosis
            selectedList={selectedProcedure}
            setSelectedDiagnosis={setSelectedProcedure}
            type={'PROCEDURE'}
            serviceDate={dateOfService}
          />
        </div>
      </div>
    </CCard>
  )
}
ProcedureCard.propTypes = {
  setSelectedProcedure: PropTypes.any,
  selectedProcedure: PropTypes.any,
}
export default ProcedureCard
